import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions-types/agent-quota-snapshot'
import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {makeFileReference, makeSnippetReference} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {ChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
import type {CopilotChatRepo, LineRange} from '@github-ui/copilot-chat-types'
import type {AhpSpaceSelection} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import type {Repository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {buildPromptWithReferenceContext as buildSharedPromptWithReferenceContext} from '@github-ui/copilot-shared-ui/composer/build-prompt-with-reference-context'

import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {EnvironmentSubmit} from './use-environment-selection'
import type {HandleUserSubmit} from './user-submit'

export const MCP_PERMISSION_GATE_FEATURE_FLAG = 'copilot_swe_agent_mcp_permission_gate'
export const DEFAULT_TASK_COMPOSER_SCOPE: TaskComposerScope = {kind: 'personal'}

export interface AvailableOrganization {
  id: string
  login: string
  name: string
  avatarUrl: string
  requiresSso: boolean
}

export type TaskComposerScope = {kind: 'personal'} | {kind: 'organization'; organization: AvailableOrganization}

export function scopeOrgLogin(scope: TaskComposerScope): string | undefined {
  return scope.kind === 'organization' ? scope.organization.login : undefined
}

/** Login Mission Control should use to scope repository-less managed compute. */
export function scopeLogin(scope: TaskComposerScope, currentUserLogin: string | undefined): string | undefined {
  return scope.kind === 'organization' ? scope.organization.login : currentUserLogin
}

/**
 * Returns true when a repo remains valid under the active scope.
 *
 * A repo is in scope when:
 *   - no scope owner is active, or
 *   - the repo owner matches the scope owner (case-insensitive).
 *
 * Both personal and org scopes filter strictly by owner, so repos owned by
 * anyone else (public or private) are dropped when a scope owner is set.
 */
export function isRepoInScope(
  repo: {owner?: string; private?: boolean} | null | undefined,
  scopeOwner: string | undefined,
): boolean {
  if (!repo) return true
  if (!scopeOwner) return true
  return (repo.owner ?? '').toLowerCase() === scopeOwner.toLowerCase()
}

/**
 * Resolves whether the MCP permission gate UI is enabled.
 * The prop is authoritative when provided (true or false); an omitted prop
 * falls back to the legacy `copilot_swe_agent_mcp_permission_gate` feature flag.
 */
export function resolveMcpPermissionGate(mcpPermissionGateEnabled?: boolean): boolean {
  return mcpPermissionGateEnabled ?? isFeatureEnabled(MCP_PERMISSION_GATE_FEATURE_FLAG)
}

export function canPrepareSandboxChat({
  chatEnabled,
  hasEnvironmentSubmit,
  showCreateTaskStrip,
  hideRepoSelector,
  showSandboxOption,
  hasSpecifiedRepository,
  scopeLogin: launchScopeLogin,
}: {
  chatEnabled: boolean
  hasEnvironmentSubmit: boolean
  showCreateTaskStrip: boolean
  hideRepoSelector: boolean
  showSandboxOption: boolean
  hasSpecifiedRepository: boolean
  scopeLogin?: string
}): boolean {
  return (
    chatEnabled &&
    hasEnvironmentSubmit &&
    showCreateTaskStrip &&
    !hideRepoSelector &&
    showSandboxOption &&
    !hasSpecifiedRepository &&
    Boolean(launchScopeLogin)
  )
}

export function canPrepareTaskComposerChat({
  chatLaunch,
  hasEnvironmentSubmit,
  showCreateTaskStrip,
  hideRepoSelector,
  showSandboxOption,
  hasSpecifiedRepository,
  scopeLogin: launchScopeLogin,
}: {
  chatLaunch?: ChatLaunchAvailability
  hasEnvironmentSubmit: boolean
  showCreateTaskStrip: boolean
  hideRepoSelector: boolean
  showSandboxOption: boolean
  hasSpecifiedRepository: boolean
  scopeLogin?: string
}): boolean {
  // A handoff neither provisions nor binds an environment, so the Sandbox
  // prerequisites below would only ever disable a destination that does not
  // need them. It still respects an explicitly specified repository, which
  // means the caller asked for a task rather than Chat.
  if (chatLaunch?.kind === 'handoff') return !hasSpecifiedRepository

  const hasAvailableLauncher =
    chatLaunch?.kind === 'legacy' ||
    (chatLaunch?.kind === 'environment' && hasEnvironmentSubmit && Boolean(launchScopeLogin))

  return (
    hasAvailableLauncher && showCreateTaskStrip && !hideRepoSelector && showSandboxOption && !hasSpecifiedRepository
  )
}

export function getDraftStorageKeySuffix(eventType: string | undefined, repoNameWithOwner: string | undefined): string {
  const base = eventType ?? ''
  return repoNameWithOwner ? `${base}:${repoNameWithOwner}` : base
}

export function getChatDraftStorageKeySuffix(eventType: string | undefined): string {
  return getDraftStorageKeySuffix(eventType, 'chat')
}

export function buildPromptWithReferenceContext(
  ...args: Parameters<typeof buildSharedPromptWithReferenceContext>
): ReturnType<typeof buildSharedPromptWithReferenceContext> {
  return buildSharedPromptWithReferenceContext(...args)
}

export type CreateTaskReference = {
  path: string
  range?: LineRange
}

export function makeFileOrSnippetReference({
  referenceRepo,
  reference,
}: {
  referenceRepo: CopilotChatRepo
  reference: CreateTaskReference
}) {
  if (reference.range) {
    return makeSnippetReference(reference.path, reference.range, referenceRepo)
  } else {
    return makeFileReference(reference.path, referenceRepo)
  }
}

export type TaskComposerProps = {
  handleUserSubmit: HandleUserSubmit
  initialRepo?: Repository | null
  /** Serializable repository identifier resolved by the task-composer repo loader. */
  initialRepositoryNameWithOwner?: string
  initialReference?: CreateTaskReference | null
  hideRepoSelector?: boolean
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  chatInputRef?: React.RefObject<ChatInputRef | null>
  className?: string
  eventType?: string
  shouldClearTextOnSubmit?: boolean
  /** Locks the composer while a cloud task submits. AHP submits always lock. */
  disableWhileSubmitting?: boolean
  initialText?: string
  /** Migrates a failed Quick Chat prompt into its scoped draft before clearing the one-shot recovery entry. */
  enableQuickChatDraftRecovery?: boolean
  initialPreparedPrompt?: PreparedPrompt
  /** Mount-scoped Chat attachments displayed in the composer before its first manual submit. */
  initialChatAttachments?: readonly MessageAttachment[]
  initialBranch?: string
  isNewConversation?: boolean
  maxTextareaHeight?: number
  submitWithModifier?: boolean
  /**
   * Called when the user submits with an AHP environment selected; when omitted,
   * submit falls back to the default cloud path. `repositoryNwo`/`baseRef` carry
   * the selected repo so the task can be provisioned as a worktree.
   */
  onEnvironmentSubmit?: EnvironmentSubmit
  /** Selects exactly one repository-less Chat creation path. */
  chatLaunch?: ChatLaunch
  /** Identifies one application of the preset that selects Sandbox Chat mode. */
  newChatPresetId?: string
  /**
   * Offers the managed-sandbox picker option (requires `onEnvironmentSubmit`).
   * Also gated by the managed-sandbox and AHP environments flags.
   */
  supportsManagedSandbox?: boolean
  agentQuotaSnapshot?: AgentQuotaSnapshot
  /**
   * Authoritative override for the MCP permission-mode selector when provided:
   * `true` forces it visible, `false` forces it hidden. When omitted, visibility
   * falls back to the `copilot_swe_agent_mcp_permission_gate` feature flag.
   */
  mcpPermissionGateEnabled?: boolean
  /**
   * Authoritative override for the create-task strip when provided. When omitted,
   * visibility falls back to the `coding_agent_create_task_strip` feature flag.
   */
  createTaskStripEnabled?: boolean
  /** Login of the current user, used to pre-fill the owner on the new repository URL. */
  currentUserLogin?: string
  /** Avatar URL of the current user, used as the personal-scope avatar in the scope picker. */
  currentUserAvatarUrl?: string
  /** Organizations the user can scope the composer to. Empty/omitted hides the scope picker. */
  availableOrganizations?: AvailableOrganization[]
  /** Explicit facade input; when present it takes precedence over URL target defaults. */
  initialTarget?: 'chat' | 'task'
}

export type ChatSubmissionMetadata = Readonly<Record<string, unknown>> & {
  space?: AhpSpaceSelection
  mcpPermissionMode?: McpPermissionMode
  /** Account the draft is scoped to, so the handoff can honor the picked owner. */
  scopeLogin?: string
}

/**
 * How an accepted Chat turn becomes a session.
 *
 * `legacy` and `environment` both provision here, so they depend on the Sandbox
 * environment being offered and selected. `handoff` does not: it records the
 * prepared turn and navigates to a surface that provisions it later, so
 * environment availability must not decide whether the turn can be composed.
 */
export type ChatLaunchAvailability = {kind: 'legacy'} | {kind: 'environment'} | {kind: 'handoff'}

export type ChatLaunch =
  | {
      kind: 'legacy'
      onSubmit: (prompt: PreparedPrompt, metadata?: ChatSubmissionMetadata) => void | Promise<void>
    }
  | {
      kind: 'environment'
    }
